"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"

import { cn } from "@/lib/utils"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
        className
      )}
      {...props}
    />
  )
}

/**
 * Open/close transition durations, as a matched pair of LITERAL class strings.
 *
 * These have to be literals a scanner can see: Tailwind infers usage by reading
 * source text, so a class assembled at runtime from a number is never emitted
 * and the transition silently falls back to the base `transition` duration.
 * A lookup keyed by a small union keeps the values literal while still letting
 * a caller choose one.
 */
const SHEET_DURATION = {
  300: "data-[state=closed]:duration-300 data-[state=open]:duration-300",
  500: "data-[state=closed]:duration-500 data-[state=open]:duration-500",
} as const

/**
 * The per-side entry/exit transforms. `fade` is side-independent by nature —
 * opacity has no direction — which is why it is a single string rather than a
 * per-side one.
 */
const SHEET_SLIDE = {
  top: "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
  right:
    "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  bottom:
    "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
  left: "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
} as const

const SHEET_FADE = "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"

/** Where the panel is pinned. Independent of how it arrives there. */
const SHEET_POSITION = {
  top: "inset-x-0 top-0 h-auto",
  right: "inset-y-0 right-0 h-full",
  bottom: "inset-x-0 bottom-0 h-auto",
  left: "inset-y-0 left-0 h-full",
} as const

/** The edge each side butts against, and so the edge that carries its rule. */
const SHEET_EDGE_BORDER = {
  top: "border-b",
  right: "border-l",
  bottom: "border-t",
  left: "border-r",
} as const

function SheetContent({
  className,
  children,
  side = "right",
  overlayClassName,
  closeButton,
  width,
  duration,
  motion = "slide",
  edgeBorder = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  /**
   * Extra classes for the portalled scrim. The scrim is a PORTAL SIBLING of
   * the panel, so a consumer cannot reach it with a descendant variant on
   * `className` — without this prop the only way to repaint one surface's
   * scrim is a global rule that repaints every other surface's too.
   * Omitted (the default) renders `cn(base, undefined)` === `cn(base)`, i.e.
   * byte-identical to the scrim every existing consumer already gets.
   */
  overlayClassName?: string
  /**
   * Replaces the built-in top-right close control. `undefined` (the default)
   * keeps that control exactly as it was for every consumer that does not
   * pass this prop; pass `null` to render no built-in close at all, which is
   * what a surface needs when it renders its own close somewhere else in the
   * panel (a brand whose close is at the panel's top LEFT, say).
   */
  closeButton?: React.ReactNode
  /**
   * The panel's width, as literal Tailwind class(es).
   *
   * WHY THIS IS A PROP AND NOT JUST `className`. The side arms ship a default
   * of `w-3/4 sm:max-w-sm`. A caller that wants a different width via
   * `className` therefore has to CANCEL that default as well as state its own,
   * and cancelling it is not one class: tailwind-merge treats a base utility
   * and its `sm:` variant as different properties, so `max-w-none` alone leaves
   * `sm:max-w-sm` in the list and the variant still wins from 640 up. Four
   * separate callers had independently discovered that and each carries a
   * paragraph explaining it.
   *
   * Passing `width` instead suppresses the default arm entirely, so there is
   * nothing to cancel. `undefined` (the default) emits `w-3/4 … sm:max-w-sm`
   * exactly as before, so a caller that does not pass it cannot move.
   */
  width?: string
  /**
   * Transition duration in ms, applied to BOTH directions.
   *
   * `undefined` (the default) keeps the primitive's historical asymmetric pair
   * — 500ms opening, 300ms closing — byte-identical for every existing caller.
   * Pass a value only for a surface whose measured motion is symmetric.
   */
  duration?: keyof typeof SHEET_DURATION
  /**
   * How the panel arrives. `slide` (the default) is today's behaviour for every
   * caller. `fade` swaps the directional transform for opacity and is what a
   * surface measured as fading needs; position, width and border are unaffected
   * either way, so this changes only the animation.
   */
  motion?: "slide" | "fade"
  /**
   * Whether the panel draws a 1px rule on the edge it butts against.
   *
   * `true` (the default) is today's behaviour. This is a prop rather than a
   * token because the sheet's border is Tailwind's built-in `border`, not the
   * `border-ui` shape token — that one applies to Card only — so there is no
   * source token to override, and the repo's shape-token rule forbids
   * neutralising it with a `border-0` utility at the call site.
   */
  edgeBorder?: boolean
}) {
  return (
    <SheetPortal>
      <SheetOverlay className={overlayClassName} />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-4 shadow-ui transition ease-in-out",
          // Emitted in this order so that with every prop left at its default
          // the pieces below concatenate to the exact class string this
          // primitive shipped before they existed — same classes, same order.
          duration === undefined
            ? "data-[state=closed]:duration-300 data-[state=open]:duration-500"
            : SHEET_DURATION[duration],
          motion === "fade" ? SHEET_FADE : SHEET_SLIDE[side],
          SHEET_POSITION[side],
          // Only the horizontal sides ever carried a default width.
          width ?? ((side === "right" || side === "left") && "w-3/4"),
          edgeBorder && SHEET_EDGE_BORDER[side],
          // The default width's responsive cap. It belongs to the DEFAULT only:
          // a caller that states its own width must not inherit a 384px ceiling
          // it never asked for and would then have to cancel.
          width === undefined &&
            (side === "right" || side === "left") &&
            "sm:max-w-sm",
          className
        )}
        {...props}
      >
        {children}
        {closeButton === undefined ? (
          <SheetPrimitive.Close className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-ui opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        ) : (
          closeButton
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 p-4", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex flex-col gap-2 p-4", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("text-foreground font-semibold", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
